import { isSafeReturnTo, submitOauthSessionBridge } from "PFApp/helpers/oauth_session_bridge";
import { useIsSignedIn } from "PFApp/hooks";
import { useAppLayoutStoreActions } from "PFApp/stores/app_layout_store";
import { useAuthStoreActions } from "PFApp/stores/auth_store";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import useIsAuditPlannerPermitted from "PFApp/use_is_audit_planner_permitted";
import { useIsMarketplacePermitted } from "PFApp/use_is_marketplace_permitted";
import useIsWorkflowPermitted from "PFApp/use_is_workflow_permitted";
import { LoadingDots } from "PFComponents/loading_dots";
import { useStoreMsTeamsLastVisitedPath } from "PFCore/helpers/ms_teams";
import { isSaasAdmin } from "PFCore/helpers/profile";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import useIsPermittedTo, { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useStringParamValue } from "PFCore/hooks";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { FeatureFlag } from "PFCore/types/feature_flag";
import { PermissionRule } from "PFTypes";
import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { startsWith } from "underscore.string";

import { useDeeplink } from "../use_deeplink";
import { isAccountSwitched, isRegistrationComplete, shouldShowTerms } from "./utils";

type WithChildrenProps = {
  children: ReactNode;
};

type WithLayoutProps = WithChildrenProps & {
  layout?: { sidebar: string };
};

export const WithLayout = ({ children, layout = { sidebar: "sidebar" } }: WithLayoutProps) => {
  const { setLayout } = useAppLayoutStoreActions();

  useEffect(() => {
    setLayout(layout.sidebar);
  }, []);

  return children;
};

export const EnsureLoggedIn = ({ children }: WithChildrenProps) => {
  const location = useLocation();
  const { isSignedIn } = useIsSignedIn();

  const { clearTokens } = useAuthStoreActions();
  useStoreMsTeamsLastVisitedPath();
  const { saveDeeplink } = useDeeplink();

  useEffect(() => {
    if (!isSignedIn) {
      if (location.pathname !== "/sign_out") {
        saveDeeplink();
      }
      clearTokens();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  if (isSignedIn) {
    return children;
  } else {
    return <Navigate to="/users/signin" replace />;
  }
};

const OAUTH_BRIDGE_FALLBACK_MS = 10000;

export const EnsureLoggedOut = ({ children }: WithChildrenProps) => {
  const { isSignedIn } = useIsSignedIn();
  const { getAccessToken } = useAuthStoreActions();
  const returnTo = useStringParamValue("return_to");
  const safeReturnTo = returnTo && isSafeReturnTo(returnTo) ? returnTo : null;
  const [bridgeFailed, setBridgeFailed] = useState(false);

  useEffect(() => {
    const accessToken = getAccessToken();
    if (!isSignedIn || !safeReturnTo || !accessToken) {
      return undefined;
    }

    const fallback = window.setTimeout(() => setBridgeFailed(true), OAUTH_BRIDGE_FALLBACK_MS);
    submitOauthSessionBridge(accessToken, safeReturnTo);
    return () => window.clearTimeout(fallback);
  }, [isSignedIn, safeReturnTo, getAccessToken]);

  if (!isSignedIn) {
    return children;
  }

  if (safeReturnTo && !bridgeFailed) {
    return <LoadingDots pageViewEnabled centered circlesEnabled qaId="EnsureLoggedOut.BridgeLoading" />;
  }

  return <Navigate to="/" replace />;
};

export const EnsureRegistrationComplete = ({ children }: WithChildrenProps) => {
  const location = useLocation();
  const { data: currentAccount } = useCurrentAccount();

  const { data: currentProfile } = useCurrentProfile();

  if (currentProfile && shouldShowTerms(currentAccount, currentProfile)) {
    return <Navigate to="/terms" replace />;
  } else if (currentProfile && isRegistrationComplete(currentProfile)) {
    const path = location.pathname;
    const isAdminPath = startsWith(path, "/admin") || startsWith(path, "/saas_admin");

    if (!isAdminPath && isAccountSwitched(currentProfile, currentAccount)) {
      return <Navigate to="/admin" replace />;
    }

    return children;
  } else {
    return <Navigate to="/onboarding" replace />;
  }
};

export const EnsureRegistrationNotComplete = ({ children }: WithChildrenProps) => {
  const { data: currentProfile } = useCurrentProfile();

  if (!isRegistrationComplete(currentProfile)) {
    return children;
  } else {
    return <Navigate to="/" replace />;
  }
};

type EnsureFeatureEnabledProps = WithChildrenProps & {
  feature: FeatureFlag;
  redirectTo?: string;
};

export const EnsureFeatureEnabled = ({ feature, redirectTo = "/", children }: EnsureFeatureEnabledProps) => {
  const isEnabled = useIsFeatureEnabled();

  if (isEnabled(feature)) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

type EnsureProfilePermissionProps = WithChildrenProps & {
  permittedTo?: string;
  redirectTo?: string;
  enabled?: boolean;
};

export const EnsureProfilePermission = ({
  permittedTo,
  redirectTo = "/",
  enabled = true,
  children
}: EnsureProfilePermissionProps) => {
  const { data: currentProfile } = useCurrentProfile();

  const isPermittedTo = useIsPermittedTo(currentProfile);

  if (!enabled || isPermittedTo(permittedTo)) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

type WithRedirectToProps = WithChildrenProps & {
  redirectTo?: string;
};

export const EnsureBookingModulePermission = ({ redirectTo = "/", children }: WithRedirectToProps) => {
  const { isEnabled, isPermittedToAccess } = useBookingModulePermissions();

  if (isEnabled && isPermittedToAccess) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

export const EnsureWorkflowPermission = ({ redirectTo = "/", children }: WithRedirectToProps) => {
  const isWorkflowPermitted = useIsWorkflowPermitted();

  if (isWorkflowPermitted) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

export const EnsureTransferPermission = ({ redirectTo = "/", children }: WithRedirectToProps) => {
  const isAllowedToTransferEngagements = useIsCurrentUserPermittedTo(PermissionRule.WorkflowTransfer);

  if (isAllowedToTransferEngagements) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

export const EnsureAuditPlannerPermission = ({ redirectTo = "/", children }: WithRedirectToProps) => {
  const auditPlannerPermitted = useIsAuditPlannerPermitted();

  if (auditPlannerPermitted) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

type EnsureIsAdminProps = WithChildrenProps & {
  limitUserManager?: boolean;
};

export const EnsureIsAdmin = ({ children, limitUserManager }: EnsureIsAdminProps) => {
  const { data: currentProfile } = useCurrentProfile();

  const isPermittedTo = useIsPermittedTo(currentProfile);

  if (limitUserManager && isPermittedTo(PermissionRule.UserManager)) {
    return <Navigate to="/" replace />;
  } else if (currentProfile.roles && currentProfile.roles.indexOf("admin") >= 0) {
    return children;
  } else {
    return <Navigate to="/" replace />;
  }
};

export const EnsureIsSaasAdmin = ({ children }: WithChildrenProps) => {
  const { data: currentProfile } = useCurrentProfile();

  if (isSaasAdmin(currentProfile)) {
    return children;
  } else {
    return <Navigate to="/" replace />;
  }
};

export const EnsureLoggedInAndRegistered = ({ children }: WithChildrenProps) => (
  <EnsureLoggedIn>
    <EnsureRegistrationComplete>{children}</EnsureRegistrationComplete>
  </EnsureLoggedIn>
);

export const EnsureMarketplacePermission = ({ redirectTo = "/", children }: WithRedirectToProps) => {
  const isMarketplacePermitted = useIsMarketplacePermitted();

  if (isMarketplacePermitted) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};
